import { getLogger } from '@cld/upload-widget-common';
import createBoundActionCreators, { createCustomCreator, createDispatcher } from 'common/actionsBase';
import rootBindActions, { types as rootTypes } from 'root/actions';
import { fileSelect } from 'tabs/common/actionMethods';
import consts from '../data/consts';
import {
  selectAuthData,
  selectIsIdSelected,
  selectReachedMaxSelection,
  selectCurrentContext,
  selectContextItems,
  selectMultipleIdsInRange,
  selectAvailableUntilMax,
} from '../selectors';
import getFacebook from '../service';

const logger = getLogger();

export const types = {
  DO_LOGIN: 'FB_DO_LOGIN',
  DO_LOGOUT: 'FB_DO_LOGOUT',
  DO_REQUEST_FB_STATUS: 'REQUEST_FB_STATUS',
  GET_CONTEXT_PHOTOS: 'GET_CONTEXT_PHOTOS',
  SET_FILE_SELECTION: 'FB_SET_FILE_SELECTION',
  SET_MULTI_FILE_SELECTION: 'FB_SET_MUTLI_FILE_SELECTION',
  SET_LOGIN_STATUS_REQUESTED: 'FB_SET_LOGIN_STATUS_REQUESTED',
  SET_LOGIN_STATUS_SUCCESS: 'FB_SET_LOGIN_STATUS_SUCCEEDED',
  SET_LOGIN_STATUS_FAILED: 'FB_SET_LOGIN_STATUS_FAILED',
  SET_USER_REQUESTED: 'FB_SET_USER_REQUESTED',
  SET_USER_FAILED: 'FB_SET_USER_FAILED',
  SET_USER_SUCCESS: 'FB_SET_USER_SUCCEEDED',
  SET_USER_LOGIN_SUCCESS: 'FB_SET_USER_LOGIN_SUCCEEDED',
  SET_USER_LOGOUT_SUCCESS: 'FB_SET_USER_LOGOUT_SUCCESS',
  SET_USER_LOGIN_FAILED: 'FB_SET_USER_LOGIN_FAILED',
  SET_USER_LOGIN_REQUESTED: 'FB_SET_USER_LOGIN_REQUESTED',
  SET_PHOTOS_RETRIEVE_START: 'FB_SET_PHOTOS_RETRIEVE_START',
  SET_PHOTOS_RETRIEVE_FAILED: 'FB_SET_PHOTOS_RETRIEVE_FAILED',
  SET_PHOTOS_RETRIEVE_SUCCESS: 'FB_SET_PHOTOS_RETRIEVE_SUCCESS',
  SET_ALBUM_RETRIEVE_SUCCESS: 'FB_SET_ALBUM_RETRIEVE_SUCCESS',
  CLEAR_IMAGE_SELECTION: 'FB_CLEAR_IMAGE_SELECTION',
  CLEAR_ALL_SELECTION: 'FB_CLEAR_ALL_SELECTION',
  CLEAR_PHOTOS_FOR_CONTEXT: 'FB_CLEAR_PHOTOS_FOR_CONTEXT',
};

const getUser = async (fbApi, auth, dispatcher) => {
  logger.log('[facebookTab.actions]: about to retrieve fb user: ', auth);

  try {
    dispatcher(types.SET_USER_REQUESTED);
    const response = await fbApi.getUser(auth);
    logger.log('[facebookTab.actions]: retrieved fb user: ', response);
    dispatcher(types.SET_USER_SUCCESS, { user: response });
  } catch (err) {
    dispatcher(types.SET_USER_FAILED, { error: err });
  }
};

const contextRetrievers = {
  [consts.CONTEXTS.UPLOADED_PHOTOS](auth, params) {
    return getFacebook().getUploadedPhotos({ auth, ...params });
  },

  [consts.CONTEXTS.TAGGED_PHOTOS](auth, params) {
    return getFacebook().getTaggedPhotos({ auth, ...params });
  },

  [consts.CONTEXTS.ALBUMS](auth, params) {
    return getFacebook().getAlbums({ auth, ...params });
  },

  [consts.CONTEXTS.ALBUM_PHOTOS]: async (auth, params, dispatcher) => {
    const results = await getFacebook().getAlbum({ auth, ...params });

    dispatcher(types.SET_ALBUM_RETRIEVE_SUCCESS, { album: results.album });

    return results;
  },
};

export default createBoundActionCreators(types, {
  [types.CLEAR_IMAGE_SELECTION]: createCustomCreator((context, dispatch) => {
    createDispatcher(dispatch)([[types.CLEAR_IMAGE_SELECTION, { context }], [rootTypes.CLEAR_MAX_SELECT_REACHED]]);
  }),

  [types.DO_REQUEST_FB_STATUS]: createCustomCreator(async (options, dispatch) => {
    const dispatcher = createDispatcher(dispatch);
    const fbApi = getFacebook();

    logger.log('[facebookTab.actions]: about to initialize facebook api');
    await fbApi.init(options);

    try {
      dispatcher(types.SET_LOGIN_STATUS_REQUESTED);

      const response = await fbApi.getLoginStatus();

      logger.log('[facebookTab.actions]: retrieved user login status: ', response);
      dispatcher(types.SET_LOGIN_STATUS_SUCCESS, { auth: response });

      await getUser(fbApi, response, dispatcher);
    } catch (err) {
      dispatch(types.SET_LOGIN_STATUS_FAILED, { error: err });
    }
  }),

  [types.DO_LOGIN]: createCustomCreator(async (dispatch) => {
    const dispatcher = createDispatcher(dispatch);
    const fbApi = getFacebook();

    try {
      dispatcher(types.SET_USER_LOGIN_REQUESTED);
      logger.log('[facebookTab.actions]: about to do user login');

      const response = await fbApi.login();

      dispatcher(types.SET_USER_LOGIN_SUCCESS, { auth: response });
      logger.log('[facebookTab.actions]: successfully logged in fb user');

      await getUser(fbApi, response, dispatcher);
    } catch (err) {
      logger.error('[facebookTab.actions]: encountered error on user login');
      dispatcher(types.SET_USER_LOGIN_FAILED, { error: err });
    }
  }),

  [types.DO_LOGOUT]: createCustomCreator(async (dispatch) => {
    const dispatcher = createDispatcher(dispatch);
    const fbApi = getFacebook();

    try {
      logger.log('[facebookTab.actions]: about to log out user');

      const result = await fbApi.logout();

      dispatcher(types.SET_USER_LOGOUT_SUCCESS, { auth: result });
    } catch (err) {
      logger.error('[facebookTab.actions]: encountered error on user logout');
    }
  }),

  [types.GET_CONTEXT_PHOTOS]: createCustomCreator(async (context, params, dispatch, getState) => {
    const dispatcher = createDispatcher(dispatch);

    try {
      dispatcher(types.SET_PHOTOS_RETRIEVE_START, { context });

      logger.log(`[facebookTab.actions]: about to get photos for context: ${context}`);

      if (params && params.extend) {
        const contextData = selectContextItems(getState());

        params = {
          ...params,
          items: contextData.items,
          cursor: contextData.cursor,
        };
      } else {
        dispatcher(types.CLEAR_PHOTOS_FOR_CONTEXT, { context });
      }

      const { auth } = selectAuthData(getState());
      const result = await contextRetrievers[context](auth, params, dispatcher);

      logger.log(`[facebookTab.actions]: finished retrieving photos for context: ${context}`);

      dispatcher(types.SET_PHOTOS_RETRIEVE_SUCCESS, { context, ...result });
    } catch (err) {
      logger.error('[facebookTab.actions]: encountered error when trying to retrieve context photos', err);
      dispatcher(types.SET_PHOTOS_RETRIEVE_FAILED, { context });
    }
  }),

  [types.SET_FILE_SELECTION]: createCustomCreator(({ id, e, multiple }, dispatch, getState) => {
    const { context } = selectCurrentContext(getState());

    fileSelect(
      {
        id,
        e,
        types: { ...rootTypes, ...types },
        rootBindActions,
        selectIsIdSelected,
        selectReachedMaxSelection,
        selectMultipleIdsInRange,
        selectAvailableUntilMax,
        selectionParams: [context],
        multiple,
      },
      dispatch,
      getState
    );
  }),
});
